/**
 * useRbacMe - React Query hook for fetching user RBAC data
 *
 * This hook provides optimized caching for the /api/rbac/me endpoint
 * with aggressive cache settings to reduce API calls and CloudFlare KV lookups.
 *
 * Cache Strategy:
 * - staleTime: 15 minutes (roles rarely change mid-session)
 * - gcTime: 30 minutes (keep data longer for returning users)
 * - No refetch on window focus or reconnect (permission changes are rare)
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useMemo } from 'react'
import { rbacApi, RbacApiError, type RbacMeData } from '@/lib/rbac-api'
import type { UserSession, Permission } from '@/types/permissions'
import type { Auth0UserWithClaims } from '@/lib/auth/auth0-mapper'
import { mapAuth0UserToUser } from '@/lib/auth/auth0-mapper'
import { DEFAULT_PERMISSION_MATRIX } from '@/lib/auth/default-permissions'
import { logger } from '@/lib/logger'
import { env } from '@/config/env'

/**
 * Query key factory for RBAC queries
 * Enables precise cache invalidation and query matching
 */
export const rbacKeys = {
  all: ['rbac'] as const,
  me: (userId: string | undefined) => [...rbacKeys.all, 'me', userId] as const,
  meWithStudio: (userId: string | undefined, studioId: string | undefined) =>
    [...rbacKeys.me(userId), studioId] as const,
}

/**
 * Cache configuration - optimized for infrequent permission changes
 */
const RBAC_CACHE_CONFIG = {
  /** How long data is considered fresh (15 minutes) */
  staleTime: 15 * 60 * 1000,
  /** How long to keep data in cache after last use (30 minutes) */
  gcTime: 30 * 60 * 1000,
  /** Don't refetch when window regains focus */
  refetchOnWindowFocus: false,
  /** Don't refetch on network reconnect */
  refetchOnReconnect: false,
  /** Single retry on failure */
  retry: 1,
} as const

/**
 * Options for useRbacMe hook
 */
export interface UseRbacMeOptions {
  /** Optional studio ID for multi-studio support */
  studioId?: string | null
  /** Whether the query should be enabled (default: true when authenticated) */
  enabled?: boolean
}

/**
 * Build a fallback session from Auth0 user data
 * Used when the API is unavailable (500 errors, network issues)
 */
function buildFallbackSession(auth0User: Auth0UserWithClaims): UserSession {
  const mockUser = mapAuth0UserToUser(auth0User)

  // Determine role from user's roles (mapping aerialist -> member, teacher -> instructor)
  // In development, allow override via VITE_DEV_ROLE_OVERRIDE env var
  let role: 'owner' | 'manager' | 'instructor' | 'member'

  if (env.IS_DEV && env.DEV_ROLE_OVERRIDE) {
    const roleMap: Record<string, 'owner' | 'manager' | 'instructor' | 'member'> = {
      owner: 'owner',
      manager: 'manager',
      teacher: 'instructor',
      aerialist: 'member',
    }
    role = roleMap[env.DEV_ROLE_OVERRIDE] || 'member'
    logger.warn(`[DEV] Role override active: ${env.DEV_ROLE_OVERRIDE} -> ${role}`)
  } else {
    role = mockUser.roles.includes('owner')
      ? 'owner'
      : mockUser.roles.includes('manager')
        ? 'manager'
        : mockUser.roles.includes('teacher')
          ? 'instructor'
          : 'member'
  }

  return {
    user: {
      id: mockUser.id,
      email: mockUser.email,
      name: mockUser.name,
      avatar: mockUser.avatar,
    },
    currentStudio: {
      studioId: 'studio-1',
      studioName: 'Aerial Flow Studio',
      studioSlug: 'aerial-flow',
      role: role,
      permissions: DEFAULT_PERMISSION_MATRIX[role],
      isPrimary: true,
    },
    allStudioMemberships: [
      {
        studioId: 'studio-1',
        studioName: 'Aerial Flow Studio',
        studioSlug: 'aerial-flow',
        role: role,
        permissions: DEFAULT_PERMISSION_MATRIX[role],
        isPrimary: true,
      },
    ],
  }
}

/**
 * Transform API response to UserSession format
 */
function transformRbacResponse(
  rbacData: RbacMeData,
  auth0User: Auth0UserWithClaims
): UserSession {
  return {
    user: {
      id: rbacData.userId,
      email: auth0User.email ?? '',
      name: auth0User.name ?? auth0User.nickname ?? '',
      avatar: auth0User.picture,
    },
    currentStudio: {
      studioId: rbacData.membership.studioId,
      studioName: rbacData.membership.studioName,
      studioSlug: rbacData.membership.studioSlug,
      role: rbacData.membership.role,
      permissions: rbacData.membership.permissions as Permission[],
      isPrimary: rbacData.membership.isPrimary,
    },
    allStudioMemberships: [
      {
        studioId: rbacData.membership.studioId,
        studioName: rbacData.membership.studioName,
        studioSlug: rbacData.membership.studioSlug,
        role: rbacData.membership.role,
        permissions: rbacData.membership.permissions as Permission[],
        isPrimary: rbacData.membership.isPrimary,
      },
    ],
  }
}

/**
 * Hook to fetch user RBAC data with optimized caching
 *
 * @example
 * ```tsx
 * const { session, isLoading, roles, permissions } = useRbacMe()
 *
 * if (isLoading) return <Spinner />
 *
 * if (permissions.includes('members:view-list')) {
 *   // Show members list
 * }
 * ```
 */
export function useRbacMe(options: UseRbacMeOptions = {}) {
  const { studioId, enabled: enabledOption } = options

  const {
    user: auth0User,
    isAuthenticated,
    getAccessTokenSilently,
  } = useAuth0()

  const queryClient = useQueryClient()

  // Determine the user ID for cache key
  const userId = auth0User?.sub

  // Build the query key with optional studio ID
  const queryKey = studioId
    ? rbacKeys.meWithStudio(userId, studioId)
    : rbacKeys.me(userId)

  // Query is enabled when authenticated and user exists (unless explicitly disabled)
  const enabled = enabledOption !== false && isAuthenticated && !!auth0User

  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<UserSession> => {
      const token = await getAccessTokenSilently()
      if (!token) {
        throw new Error('No access token available')
      }

      if (!auth0User) {
        throw new Error('No user available')
      }

      try {
        const response = await rbacApi.getMe(token, studioId ?? undefined)

        const session = transformRbacResponse(
          response.data,
          auth0User as Auth0UserWithClaims
        )

        logger.debug('[RBAC] Session loaded from API', {
          userId: session.user.id,
          studioId: session.currentStudio?.studioId,
          role: session.currentStudio?.role,
        })

        return session
      } catch (error) {
        // Handle specific API errors
        if (error instanceof RbacApiError) {
          // 401 errors indicate auth issues - rethrow to trigger reauth
          if (error.requiresReauth) {
            logger.warn('[RBAC] Token expired or invalid, needs reauth')
            throw error
          }

          // 500 errors or JWKS issues - fall back to mock data
          if (error.isRetryable) {
            logger.warn('[RBAC] API error, using fallback session', {
              status: error.status,
              code: error.code,
            })
            return buildFallbackSession(auth0User as Auth0UserWithClaims)
          }
        }

        // Network errors or other issues - fall back to mock data
        logger.warn('[RBAC] Failed to fetch from API, using fallback', { error })
        return buildFallbackSession(auth0User as Auth0UserWithClaims)
      }
    },
    enabled,
    ...RBAC_CACHE_CONFIG,
  })

  // Invalidate cache and refetch
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: rbacKeys.me(userId) })
  }, [queryClient, userId])

  // Refetch session
  const refetch = useCallback(async () => {
    await query.refetch()
  }, [query])

  // Derived values for convenience
  const session = query.data
  const currentStudio = session?.currentStudio
  const roles = useMemo(() => currentStudio?.role ? [currentStudio.role] : [], [currentStudio])
  const permissions = useMemo(() => currentStudio?.permissions ?? [], [currentStudio])
  const membership = currentStudio

  return {
    // Query state
    data: query.data,
    session,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,

    // Actions
    refetch,
    invalidate,

    // Derived values
    userId,
    roles,
    permissions,
    membership,
  }
}
